import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { signOut } from '../features/authSlice';
import {
  addEmail,
  addMessage,
  processQueuedEvents,
  queueEvent,
  setAppPegaWebEmbedProps,
  setAppUrl,
  setBrowserUrl,
  setCalendarDate,
  setCallInfo,
  setCallInProgress,
  setCallType,
  setChannel,
  setDxaProps,
  setEmployeePaneSrc,
  setEmployeePegaWebEmbedProps,
  setOnboardingTourSteps,
  setPendingMessage,
  updateConfig
} from "../features/channelSlice";
import { addNotification } from "../features/notificationSlice";
import { setSidePanelExpanded } from "../features/sidePanelSlice";
import authService from '../services/authService';
import { sendCaraQuit, sendCaraStart } from '../utils/caraBridge';
import { resetExperience, teardownExperience } from '../utils/resetExperience';
import { publishToast } from "../utils/toastUtils";
import tokenManager from '../utils/tokenManager';
import { fetchWsTicket } from '../utils/wsTicket';

// Simplified Customer WebSocket Hook
function useCustomerWebSocket() {
  const dispatch = useDispatch();
  const {
    channelConfig,
    currentChannel,
    followMode,
    isViewingEmail,
    isCallInProgress,
    status: configStatus,
    channelConfig: { employee: employeeConfig },
    queuedEvents = [],
    configId,
    brandName,
    employeeEventsEnabled,
    onboardingEvents = []
  } = useSelector((state) => state.channel);

  const { isAuthenticated } = useSelector((state) => state.auth);
  const { isLiveModalOpen } = useSelector((state) => state.live);
  const hostServer = useSelector((state) => state.channel.hostServer);
  const applicationAlias = useSelector((state) => state.channel.applicationAlias);
  const persona = useSelector((state) => state.channel.persona);
  const [status, setStatus] = useState('disconnected');
  const [forceReconnectTrigger, setForceReconnectTrigger] = useState(0);

  // Use ref to track live modal state without causing WebSocket reconnections
  const isLiveModalOpenRef = useRef(isLiveModalOpen);

  // Use refs for values needed in queued event processing
  const followModeRef = useRef(followMode);
  const currentChannelRef = useRef(currentChannel);
  const isViewingEmailRef = useRef(isViewingEmail);
  const isCallInProgressRef = useRef(isCallInProgress);
  const channelConfigRef = useRef(channelConfig);
  const brandNameRef = useRef(brandName);
  const employeeEventsEnabledRef = useRef(employeeEventsEnabled);
  const onboardingEventsRef = useRef(onboardingEvents);

  // Update refs when values change
  useEffect(() => {
    isLiveModalOpenRef.current = isLiveModalOpen;
  }, [isLiveModalOpen]);

  useEffect(() => {
    followModeRef.current = followMode;
    currentChannelRef.current = currentChannel;
    isViewingEmailRef.current = isViewingEmail;
    isCallInProgressRef.current = isCallInProgress;
    channelConfigRef.current = channelConfig;
    brandNameRef.current = brandName;
    employeeEventsEnabledRef.current = employeeEventsEnabled;
    onboardingEventsRef.current = onboardingEvents;
  }, [followMode, currentChannel, isViewingEmail, isCallInProgress, channelConfig, brandName, employeeEventsEnabled, onboardingEvents]);

  useEffect(() => {
    if (!isAuthenticated || !configId || configStatus !== 'loaded') {
      console.log('🔴 Customer WS: Prerequisites not met, not connecting:', {
        isAuthenticated,
        configId: !!configId,
        configStatus
      });
      setStatus('disconnected');
      return;
    }

    console.log('🟢 Customer WS: All prerequisites met, connecting...', {
      isAuthenticated,
      hostServer,
      applicationAlias,
      configStatus
    });

    let ws;
    let reconnectTimer;
    let connectTimer;
    let keepaliveTimer;
    let reconnectAttempts = 0;
    let ticketRetried = false;
    let sessionId; // Session ID for backend tracking
    let isCleaningUp = false; // Race condition prevention
    let lastActivity = Date.now();
    const maxReconnectAttempts = 5;
    const keepaliveInterval = 30000; // 30 seconds
    const keepaliveTimeout = 40000; // 40 second timeout for keepalive response (increased for stability)

    async function connect() {
      if (reconnectAttempts >= maxReconnectAttempts) {
        console.log('Customer WS: Max reconnect attempts reached, giving up');
        setStatus('failed');
        return;
      }

      try {
        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log(`Customer WS: Attempting to get auth token... (attempt ${reconnectAttempts + 1}/${maxReconnectAttempts})`);
        }
        const token = await authService.getToken();
        const cognitoSub = authService.getSub();
        if (!token) {
          console.warn('Customer WS: No authentication token available');
          reconnectAttempts++;
          if (reconnectAttempts < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 5000);
            console.log(`Customer WS: Retrying in ${delay}ms...`);
            reconnectTimer = setTimeout(connect, delay);
          } else {
            console.log('Customer WS: Max attempts reached, stopping retries');
            setStatus('failed');
          }
          return;
        }

        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('Customer WS: Got auth token, fetching WS ticket...');
        }

        let ticket;
        try {
          ticket = await fetchWsTicket(token);
        } catch (err) {
          console.warn('Customer WS: Failed to fetch WebSocket ticket:', err.message);
          setStatus('failed');
          return;
        }

        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const apiBaseURL = process.env.REACT_APP_WEBSOCKET_URL || 'wss://api.ask-cara.com/ws';
        let wsUrl = `${wsProtocol}//${apiBaseURL.replace(/wss?:\/\//, '').replace('/ws', '')}/ws/customer?ticket=${ticket}&config_id=${configId}&persona=${persona || ''}${cognitoSub ? `&follow_id=${cognitoSub}` : ''}`;

        // Include session_id in reconnection attempts to maintain session continuity
        if (sessionId) {
          wsUrl += `&session_id=${sessionId}`;
        }

        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('Customer WS: Connecting...');
        }

        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('Customer WS connected');
          setStatus('connected');
          reconnectAttempts = 0;
          ticketRetried = false;
          lastActivity = Date.now();
          startKeepalive();
        };

        ws.onclose = (event) => {
          if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
            console.log('Customer WS closed:', { code: event.code, reason: event.reason, wasClean: event.wasClean });
          }

          clearTimeout(keepaliveTimer);
          setStatus('disconnected');

          // Ticket missing or expired — retry once with a fresh ticket
          if (event.code === 4001 || event.code === 4003) {
            if (!ticketRetried) {
              ticketRetried = true;
              console.warn('🔐 Customer WS: Ticket rejected (code', event.code, '), retrying with fresh ticket');
              reconnectTimer = setTimeout(connect, 0);
            } else {
              console.warn('🔐 Customer WS: Ticket retry failed, giving up');
              setStatus('failed');
            }
            return;
          }

          // Check if closure was due to authentication failure (code 4401 or 1008)
          if (event.code === 4401 || event.code === 1008 || (event.reason && event.reason.includes('auth'))) {
            console.warn('🔐 Customer WS closed due to authentication failure');
            // Check if token is expired
            tokenManager.isTokenExpired().then(expired => {
              if (expired) {
                console.warn('🔐 Token expired, triggering sign out');
                dispatch(signOut());
              }
            });
            return;
          }

          // Don't reconnect if we're cleaning up or if it was a normal close
          if (isCleaningUp || event.code === 1000) {
            if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
              console.log('Customer WS: Not reconnecting (cleanup or normal close)');
            }
            return;
          }

          if (reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
            console.log(`Customer WS: Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${maxReconnectAttempts})`);
            reconnectTimer = setTimeout(connect, delay);
          } else {
            console.log('Customer WS: Max reconnect attempts reached after close');
            setStatus('failed');
          }
        };

        ws.onerror = (error) => {
          console.error('Customer WS error:', error);
        };

        ws.onmessage = (event) => {
          // Update lastActivity on ANY message received (including empty heartbeats)
          lastActivity = Date.now();

          try {
            // Check if message data is empty or invalid - these are backend heartbeats
            if (!event.data || event.data.trim() === '') {
              if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
                console.log('Customer WS: Received empty heartbeat message');
              }
              return;
            }

            const msg = JSON.parse(event.data);

            // Handle session initialization
            if (msg.type === 'session_init' && msg.session_id) {
              sessionId = msg.session_id;
              if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
                console.log('Customer WS: Session ID received:', sessionId);
              }
              return;
            }

            // Handle keepalive responses
            if (msg.type === 'pong') {
              if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
                console.log('Customer WS: Keepalive pong received');
              }
              return;
            }

            // Handle ping requests from server
            if (msg.type === 'ping') {
              if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'pong' }));
              }
              return;
            }

            // If live modal is open, queue the event instead of processing it
            if (isLiveModalOpenRef.current && ['sms', 'email', 'calendar', 'browser', 'app', 'call', 'offer', 'push'].includes(msg.type)) {
              console.log('📥 Live modal is open, queuing event:', msg.type);
              dispatch(queueEvent(msg));
              return;
            }

            // Message handlers
            switch (msg.type) {
              case 'sms':
                if (followMode && currentChannel !== 'agent') {
                  dispatch(setPendingMessage(msg.payload));
                  dispatch(setChannel('sms'));
                } else if (currentChannel === 'sms') {
                  dispatch(setPendingMessage(msg.payload));
                } else if (msg.payload.sender !== 'me') {
                  dispatch(addMessage(msg.payload));
                  const senderName = msg.payload.sender?.toLowerCase() === 'them' ? (brandName || 'Customer Services') : (msg.payload.sender || 'Unknown');
                  dispatch(addNotification({
                    title: senderName,
                    message: msg.payload.text,
                    type: 'info',
                    channel: 'sms'
                  }));
                }
                break;

              case 'email':
                const newEmail = {
                  id: msg.payload.id || `email-${Date.now()}`,
                  senderName: msg.payload.senderName,
                  subject: msg.payload.subject,
                  timestamp: msg.payload.timestamp || new Date().toISOString(),
                  body: msg.payload.body,
                  attachments: msg.payload.attachments || [],
                  isRead: false,
                };
                dispatch(addEmail(newEmail));

                if (followMode && currentChannel !== 'agent') {
                  if (currentChannel !== 'email') {
                    dispatch(setChannel('email'));
                  } else if (isViewingEmail) {
                    dispatch(addNotification({
                      title: brandName || newEmail.senderName || 'Unknown Sender',
                      message: newEmail.subject,
                      type: 'info',
                      channel: 'email'
                    }));
                  }
                } else {
                  if (currentChannel !== 'email' || isViewingEmail) {
                    dispatch(addNotification({
                      title: brandName || newEmail.senderName || 'Unknown Sender',
                      message: newEmail.subject,
                      type: 'info',
                      channel: 'email'
                    }));
                  }
                }
                break;

              case 'calendar':
                dispatch(setCalendarDate(msg.payload.date));
                if (followMode && currentChannel !== 'agent') {
                  dispatch(setChannel('calendar'));
                } else if (currentChannel !== 'calendar') {
                  dispatch(addNotification({
                    message: `Calendar updated`,
                    type: 'info',
                    channel: 'calendar'
                  }));
                }
                break;

              case 'browser':
                dispatch(setBrowserUrl(msg.payload.url));
                if (followMode && currentChannel !== 'agent') {
                  dispatch(setChannel('browser'));
                } else if (currentChannel !== 'browser') {
                  dispatch(addNotification({
                    message: 'New content available in browser',
                    type: 'info',
                    channel: 'browser'
                  }));
                }
                break;

              case 'app':
                console.log('🔍 DEBUG: Full msg object:', JSON.stringify(msg, null, 2));
                console.log('🔍 DEBUG: msg.payload:', msg.payload);
                console.log('🔍 DEBUG: msg.payload.type:', msg.payload?.type);
                console.log('🔍 DEBUG: msg.payload.pegawebembed:', msg.payload?.pegawebembed);

                if (msg.payload.type === 'url') {
                  dispatch(setAppUrl(msg.payload.url));
                  dispatch(setAppPegaWebEmbedProps(null)); // Clear PegaWebEmbed props when switching to URL
                  dispatch(setDxaProps(null)); // Clear DXA props when switching to URL
                } else if (msg.payload.type === 'pegawebembed') {
                  dispatch(setAppUrl(null)); // Clear URL when switching to PegaWebEmbed
                  dispatch(setDxaProps(null)); // Clear DXA props when switching to PegaWebEmbed
                  dispatch(setAppPegaWebEmbedProps({
                    type: 'pegawebembed', // Add type field so AppView knows to render PegaWebEmbed
                    ...msg.payload.pegawebembed
                  }));
                } else if (msg.payload.type === 'dxa') {
                  // DXA type: Set DXA props with all parameters from payload
                  // Support both flat structure and nested under 'pegawebembed' for backward compatibility
                  const dxaParams = msg.payload.pegawebembed || msg.payload;

                  console.log('🔵 DXA Handler (live messages):', {
                    hasPegaWebEmbed: !!msg.payload.pegawebembed,
                    dxaParams: dxaParams,
                    action: dxaParams.action,
                    assignmentId: dxaParams.assignmentId || dxaParams.assignmentID,
                    caseId: dxaParams.caseId || dxaParams.caseID
                  });

                  dispatch(setAppUrl(null)); // Clear URL when switching to DXA
                  dispatch(setAppPegaWebEmbedProps(null)); // Clear PegaWebEmbed props when switching to DXA
                  dispatch(setDxaProps({
                    type: 'dxa',
                    action: dxaParams.action,
                    caseId: dxaParams.caseId || dxaParams.caseID,
                    assignmentId: dxaParams.assignmentId || dxaParams.assignmentID,
                    pageClass: dxaParams.pageClass,
                    pageID: dxaParams.pageID,
                    params: dxaParams.params
                  }));
                }
                if (followMode && currentChannel !== 'agent') {
                  dispatch(setChannel('app'));
                } else if (currentChannel !== 'app') {
                  dispatch(addNotification({
                    message: 'New content available in app',
                    type: 'info',
                    channel: 'app'
                  }));
                }
                break;

              case 'call':
                if (msg.payload) {
                  dispatch(setCallInfo(msg.payload));
                  const callType = msg.payload.callType || 'video';
                  dispatch(setCallType(callType));
                  if (callType === 'video') {
                    sendCaraStart();
                  }
                  if (!isCallInProgress) {
                    dispatch(setChannel('call'));
                  }
                }
                break;

              case 'offer':
              case 'push':
                // `push` is a clone of `offer` on the backend — same payload
                // shape, same notification behaviour. Channel is set from
                // msg.type so downstream filtering can still distinguish them
                // if needed.
                dispatch(addNotification({
                  image: msg.payload.image,
                  title: msg.payload.title,
                  message: msg.payload.message,
                  type: 'info',
                  channel: msg.type,
                  action: msg.payload.action
                }));
                break;

              case 'sidepanel':
                if (employeeConfig?.visible === false) return;
                if (!employeeEventsEnabledRef.current) {
                  console.log('🙈 Employee events disabled — ignoring sidepanel event');
                  return;
                }

                if (msg.payload.type === 'url') {
                  dispatch(setEmployeePaneSrc(msg.payload.url));
                  dispatch(setEmployeePegaWebEmbedProps(null));
                } else if (msg.payload.type === 'pegawebembed' && msg.payload.pegawebembed) {
                  dispatch(setEmployeePegaWebEmbedProps(msg.payload.pegawebembed));
                  if (msg.payload.title) {
                    dispatch(updateConfig({ sidePanelTitle: msg.payload.title }));
                  }
                }
                dispatch(setSidePanelExpanded(true));
                break;

              case 'toast':
                // Handle toast notifications - dispatch event for App.js to catch
                console.log('📬 Toast notification received from WebSocket:', msg.payload);
                publishToast(msg.payload.subject, msg.payload.body, msg.payload.icon);
                break;

              case 'home':
                // Navigate to home screen with animation when home command is received
                window.dispatchEvent(new CustomEvent('trigger-home-animation'));
                break;

              case 'nudge':
                // Trigger the GuidedTour nudge overlay used by onboarding `tour`
                // events. Payload shape:
                //   { steps: [{ target, content, title?, mode: 'nudge', placement?, ... }, ...],
                //     home?: boolean   // if true, navigate to home first so the
                //                       // nudge's target is on screen }
                if (!Array.isArray(msg.payload?.steps) || msg.payload.steps.length === 0) {
                  console.warn('🪧 Nudge event ignored — empty or missing steps payload');
                  break;
                }
                if (msg.payload.home === true) {
                  window.dispatchEvent(new CustomEvent('trigger-home-animation'));
                  // Small delay so the home animation starts before the nudge
                  // measures its target. GuidedTour's lock effect will also
                  // bounce non-home channels back, but firing home explicitly
                  // gives the user the requested visual transition.
                  setTimeout(() => {
                    dispatch(setOnboardingTourSteps(msg.payload.steps));
                  }, 200);
                } else {
                  dispatch(setOnboardingTourSteps(msg.payload.steps));
                }
                break;

              case 'callEnd':
              case 'endCall':
                // Programmatically end an active call. Clearing isCallInProgress
                // and navigating off the call channel unmounts the VoiceCallView
                // / VideoCallView, whose cleanup useEffect disconnects the
                // LiveKit room and plays the end-call tone.
                console.log('📵 callEnd event received — ending active call');
                dispatch(setCallInProgress(false));
                if (currentChannelRef.current === 'call') {
                  dispatch(setChannel('home'));
                }
                break;

              case 'reset':
                // Server-driven Reset — same staged teardown + onboarding
                // replay as the Reset button in Actions / FooterCarousel.
                console.log('🔄 Reset event received via WebSocket');
                resetExperience({
                  dispatch,
                  onboardingEvents: onboardingEventsRef.current,
                });
                break;

              case 'quit':
                // Mimic the Quit/Sign-out button without the confirm dialog
                // (the user didn't initiate this — the agent did). Native iOS
                // bridge gets the message first; web falls through to signOut.
                console.log('🚪 Quit event received via WebSocket');
                if (sendCaraQuit()) {
                  teardownExperience({ dispatch });
                } else {
                  teardownExperience({ dispatch }).then(() => dispatch(signOut()));
                }
                break;

              default:
                console.log('Unknown message type:', msg.type);
            }
          } catch (error) {
            console.error('Error parsing customer message:', error);
            console.error('Received data:', event?.data ? `"${event.data.substring(0, 100)}"` : 'undefined');
          }
        };

      } catch (error) {
        console.error('Customer WS connection error:', error);
        reconnectAttempts++;
        if (reconnectAttempts < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
          console.log(`Customer WS: Reconnecting after error in ${delay}ms (attempt ${reconnectAttempts}/${maxReconnectAttempts})`);
          reconnectTimer = setTimeout(connect, delay);
        } else {
          console.log('Customer WS: Max reconnect attempts reached after error');
          setStatus('failed');
        }
      }
    }

    function startKeepalive() {
      if (keepaliveTimer) {
        clearTimeout(keepaliveTimer);
      }

      const timeSinceLastActivity = Date.now() - lastActivity;

      // Calculate next check time - at least 1 second, at most keepaliveInterval
      let nextCheckDelay;
      if (timeSinceLastActivity >= keepaliveInterval) {
        // We're past the ping interval, check soon
        nextCheckDelay = 1000;
      } else {
        // Schedule next check for when we reach the ping interval
        nextCheckDelay = keepaliveInterval - timeSinceLastActivity;
      }

      keepaliveTimer = setTimeout(() => {
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          return; // Connection closed, stop keepalive
        }

        const currentTimeSinceLastActivity = Date.now() - lastActivity;

        // Only check for timeout if we're past the grace period
        if (currentTimeSinceLastActivity > keepaliveInterval + keepaliveTimeout) {
          console.warn('Customer WS: Keepalive timeout, forcing reconnect');
          ws.close(1000, 'Keepalive timeout');
          return; // Don't reschedule after closing
        }

        // Send keepalive ping if enough time has passed
        if (currentTimeSinceLastActivity >= keepaliveInterval) {
          console.log('Customer WS: Sending keepalive ping');
          try {
            ws.send(JSON.stringify({ type: 'ping' }));
            // Update lastActivity after sending ping so we don't send another immediately
            lastActivity = Date.now();
          } catch (error) {
            console.error('Customer WS: Error sending keepalive ping:', error);
          }
        }

        // Schedule next keepalive check
        startKeepalive();
      }, nextCheckDelay);
    }

    // Debounce initial connection to prevent rapid reconnects when effect re-runs
    connectTimer = setTimeout(connect, 100);

    return () => {
      isCleaningUp = true;
      clearTimeout(reconnectTimer);
      clearTimeout(connectTimer);
      clearTimeout(keepaliveTimer);
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, hostServer, applicationAlias, persona, configStatus, forceReconnectTrigger, dispatch, currentChannel, followMode, isViewingEmail, isCallInProgress, channelConfig, employeeConfig, brandName]);

  // Process queued events when live modal closes
  useEffect(() => {
    console.log('🔔 Queue processor useEffect triggered:', {
      isLiveModalOpen,
      queueLength: queuedEvents?.length || 0,
      hasQueue: queuedEvents && queuedEvents.length > 0
    });

    // Only process when modal is closed AND we have events in queue
    if (!isLiveModalOpen && queuedEvents && queuedEvents.length > 0) {
      console.log(`🎯 Processing ${queuedEvents.length} queued events after live modal closed`);

      // Create a copy of the queue before processing to avoid issues with state changes
      const eventsToProcess = [...queuedEvents];

      // Clear the queue first to prevent reprocessing
      dispatch(processQueuedEvents());

      // Then process each event
      eventsToProcess.forEach((msg, index) => {
        console.log(`📦 Processing queued event ${index + 1}/${eventsToProcess.length}:`, msg.type);

        switch (msg.type) {
          case 'sms':
            if (followModeRef.current && currentChannelRef.current !== 'agent') {
              dispatch(setPendingMessage(msg.payload));
              dispatch(setChannel('sms'));
            } else if (currentChannelRef.current === 'sms') {
              dispatch(setPendingMessage(msg.payload));
            } else if (msg.payload.sender !== 'me') {
              dispatch(addMessage(msg.payload));
              dispatch(addNotification({
                title: brandNameRef.current || 'Customer Services',
                message: msg.payload.text,
                type: 'info',
                channel: 'sms'
              }));
            }
            break;

          case 'email':
            const newEmail = {
              id: msg.payload.id || `email-${Date.now()}`,
              senderName: msg.payload.senderName,
              subject: msg.payload.subject,
              timestamp: msg.payload.timestamp || new Date().toISOString(),
              body: msg.payload.body,
              attachments: msg.payload.attachments || [],
              isRead: false,
            };
            dispatch(addEmail(newEmail));

            if (followModeRef.current && currentChannelRef.current !== 'agent') {
              if (currentChannelRef.current !== 'email') {
                dispatch(setChannel('email'));
              } else if (isViewingEmailRef.current) {
                dispatch(addNotification({
                  title: brandNameRef.current || newEmail.senderName || 'Unknown Sender',
                  message: newEmail.subject,
                  type: 'info',
                  channel: 'email'
                }));
              }
            } else {
              if (currentChannelRef.current !== 'email' || isViewingEmailRef.current) {
                dispatch(addNotification({
                  title: brandNameRef.current || newEmail.senderName || 'Unknown Sender',
                  message: newEmail.subject,
                  type: 'info',
                  channel: 'email'
                }));
              }
            }
            break;

          case 'calendar':
            dispatch(setCalendarDate(msg.payload.date));
            if (followModeRef.current && currentChannelRef.current !== 'agent') {
              dispatch(setChannel('calendar'));
            } else if (currentChannelRef.current !== 'calendar') {
              dispatch(addNotification({
                message: `Calendar updated`,
                type: 'info',
                channel: 'calendar'
              }));
            }
            break;

          case 'browser':
            dispatch(setBrowserUrl(msg.payload.url));
            if (followModeRef.current && currentChannelRef.current !== 'agent') {
              dispatch(setChannel('browser'));
            } else if (currentChannelRef.current !== 'browser') {
              dispatch(addNotification({
                message: 'New content available in browser',
                type: 'info',
                channel: 'browser'
              }));
            }
            break;

          case 'app':
            if (msg.payload.type === 'url') {
              dispatch(setAppUrl(msg.payload.url));
            } else if (msg.payload.type === 'pegawebembed') {
              dispatch(setAppPegaWebEmbedProps(msg.payload.pegawebembed));
            } else if (msg.payload.type === 'dxa') {
              // Handle DXA navigation request
              // Support both flat structure and nested under 'pegawebembed' for backward compatibility
              const dxaParams = msg.payload.pegawebembed || msg.payload;

              console.log('🟣 DXA Handler (queued messages):', {
                hasPegaWebEmbed: !!msg.payload.pegawebembed,
                dxaParams: dxaParams,
                action: dxaParams.action,
                assignmentId: dxaParams.assignmentId || dxaParams.assignmentID,
                caseId: dxaParams.caseId || dxaParams.caseID
              });

              dispatch(setDxaProps({
                type: 'dxa',
                action: dxaParams.action,
                pageId: dxaParams.pageId || dxaParams.pageID,
                pageClass: dxaParams.pageClass,
                authFlow: dxaParams.authFlow,
                caseId: dxaParams.caseId || dxaParams.caseID,
                assignmentId: dxaParams.assignmentId || dxaParams.assignmentID,
                workId: dxaParams.workId || dxaParams.workID,
                additionalParams: dxaParams.additionalParams || dxaParams.params
              }));
            }
            if (followModeRef.current && currentChannelRef.current !== 'agent') {
              dispatch(setChannel('app'));
            } else if (currentChannelRef.current !== 'app') {
              dispatch(addNotification({
                message: 'New content available in app',
                type: 'info',
                channel: 'app'
              }));
            }
            break;

          case 'call':
            if (msg.payload) {
              dispatch(setCallInfo(msg.payload));
              const callType = msg.payload.callType || 'video';
              dispatch(setCallType(callType));
              if (callType === 'video') {
                sendCaraStart();
              }
              if (!isCallInProgressRef.current) {
                dispatch(setChannel('call'));
              }
            }
            break;

          case 'offer':
          case 'push':
            dispatch(addNotification({
              image: msg.payload.image,
              title: msg.payload.title,
              message: msg.payload.message,
              type: 'info',
              channel: msg.type,
              action: msg.payload.action
            }));
            break;

          default:
            break;
        }
      });

      console.log('✅ All queued events processed');
    }
  }, [isLiveModalOpen, queuedEvents, dispatch]); // Track queuedEvents array for processing

  const forceReconnect = () => {
    setForceReconnectTrigger(prev => prev + 1);
  };

  return { status, forceReconnect };
}

export default useCustomerWebSocket;
